import React, { useContext, useEffect, useRef, useState } from "react";
import axios from "axios";
import { API_BASE_URL } from "@config/config";
import useBypassConfig from "@hooks/useBypassConfig";
import useContactDetails from "@hooks/useContactDetails";
import useModal from "@hooks/useModal";
import useWhiteLabelConfig from "@hooks/useWhiteLabelConfig";
import { ApplicationContext } from "@context/ApplicationContext";
import { AuthContext } from "@context/AuthContext";
import { BookSessionContext } from "@context/BookSessionContext";
import { CheckoutComplimentaryContext } from "@context/CheckoutComplimentaryContext";
import { PromoContext } from "@context/PromoContext";
import CinemaBookingOption from "@components/CinemaBookingOption";
import Button from "@components/Ui/Button";
import ContactDetailsForm from "@components/V3/ContactDetailsForm";
import Drawer from "@components/V3/Drawer";
import TermsCheckbox from "@components/V3/TermsCheckbox";
import VoucherNotice from "@components/V3/VoucherNotice";
import getCinemaGroupName from "@utils/getCinemaGroupName";
import "./style.scss";

const TermsDrawer = ({ session = null, showTotal = true }) => {
    const { openModalWithContent, portal } = useModal();
    const {
        drawerState,
        closeDrawer,
        openDrawer,
        pendingActionUrl,
        user,
    } = useContext(AuthContext);
    const { isStripeEnabled } = useWhiteLabelConfig();
    const { whiteLabel } = useContext(ApplicationContext);
    const {
        selectedItems,
        termsAccepted,
        setTermsAccepted,
        fetchReInitialiseEwayTicket,
        fetchInitialiseStripe,
        fetchInitialiseStripeTicket,
        uuid,
        cinemaBookingStatus,
        cinemaBookingFee,
        cinemaUpgradeFee,
        holdVersion,
        isPolling,
        checkBookWithCinemaStatus,
        postPurchase,
        setProceedToCheckout,
    } = useContext(BookSessionContext);

    const { appliedPromo } = useContext(PromoContext);
    const { checkoutComplementary } = useContext(CheckoutComplimentaryContext);

    const cinemaGroupName = getCinemaGroupName(session?.cinema_group_id);

    const useAgenticAi = (
        whiteLabel?.enable_agentic_ai === true
        && session?.cinema_group_id != null
        && (whiteLabel?.agentic_ai_cinema_groups ?? []).map(Number).includes(Number(session.cinema_group_id))
        && session?.ticket_class_id != null
        && (whiteLabel?.agentic_ai_ticket_classes ?? []).map(Number).includes(Number(session.ticket_class_id))
    );

    const [selectedOption, setSelectedOption] = useState("vouchers");
    const [hasUserClicked, setHasUserClicked] = useState(false);
    const [showLoader, setShowLoader] = useState(false);

    const showCinemaFees = selectedOption === "cinema" && cinemaBookingStatus === 1;
    // Fees are still being quoted, so the total on screen is not the one to check out with.
    const isFetchingCinemaFees = selectedOption === "cinema" && (isPolling || showLoader);



    const { isBypassActive, promptUserForEmail, shouldShowDisplayName } = useBypassConfig();

    const {
        purchaseEmail,
        setPurchaseEmail,
        firstName,
        setFirstName,
        lastName,
        setLastName,
        emailError,
        setEmailError,
        validateContactEmail,
        isContactDetailsValid,
    } = useContactDetails({ isBypassActive, promptUserForEmail, shouldShowDisplayName, user });

    const subTotal = Math.max(
        selectedItems.reduce((carry, item) =>
            item.promo ? carry : carry + (item.price_cents * item.quantity), 0),
        0
    );
    const discount = appliedPromo?.applied ? appliedPromo?.discount : 0;
    const ticketsTotal = Math.max(subTotal - discount, 0);
    const bookingFeeQuantity = selectedItems.reduce((carry, item) =>
        item.type === "candy-bar" ? carry : carry + (Number(item.quantity) || 0), 0);
    const cinemaFeesCents = (selectedOption === "cinema" && showCinemaFees)
        ? Math.round((cinemaBookingFee ?? 0) * 100) * (bookingFeeQuantity || 1)
            + Math.round((cinemaUpgradeFee ?? 0) * 100)
        : 0;
    const finalTotal = ticketsTotal + cinemaFeesCents;

    useEffect(() => {
        if (useAgenticAi) {
            setSelectedOption("cinema");
        }
    }, [useAgenticAi]);

    useEffect(() => {
        if (cinemaBookingStatus === 1) {
            setShowLoader(false);
        }
    }, [cinemaBookingStatus]);

    // Which hold the fees on screen belong to, so a re-hold triggers a fresh quote.
    const feesRequestedForRef = useRef(null);

    const requestCinemaFees = () => {
        feesRequestedForRef.current = `${uuid}:${holdVersion}`;
        setShowLoader(true);

        postPurchase(uuid)
            .then(() => {
                checkBookWithCinemaStatus(uuid);
            })
            .catch(error => {
                console.error("Error in postPurchase:", error);
                setShowLoader(false);
            });
    };

    // Updating the checkout re-holds the purchase, so the fees quoted for the previous
    // cart no longer apply - ask the bot for them again instead of showing the old ones.
    useEffect(() => {
        if (!hasUserClicked || selectedOption !== "cinema" || !uuid) return;
        if (feesRequestedForRef.current === `${uuid}:${holdVersion}`) return;

        requestCinemaFees();
    }, [hasUserClicked, selectedOption, uuid, holdVersion]);

    const handleOptionSelect = (option) => {
        setSelectedOption(option);
        setHasUserClicked(true);

        if (option === "cinema" && uuid) {
            requestCinemaFees();
        } else {
            setShowLoader(false);
        }
    };

    const handleContinue = async () => {
        if (isBypassActive) {
            if (!validateContactEmail()) return;
            try {
                await axios.put(`${API_BASE_URL}/ticket/purchase-email/${uuid}`, {
                    purchase_email: purchaseEmail,
                    ...(shouldShowDisplayName && { first_name: firstName, last_name: lastName }),
                });
            } catch {
                setEmailError("Failed to save email. Please try again.");
                return;
            }
        }

        setProceedToCheckout(true);
        closeDrawer("termsDrawer");

        // Stripe groups fetch an Embedded Checkout session, then open the drawer once the
        // client_secret is ready. Free bookings (nothing to pay) skip Stripe entirely.
        const isFreeCheckout = Boolean(uuid) && finalTotal <= 0 && selectedOption !== "cinema";
        if (isStripeEnabled && !isFreeCheckout) {
            // Open drawer for perceived speed...
            openDrawer("stripeCheckoutDrawer");

            const initialise = selectedOption === "cinema"
                ? fetchInitialiseStripeTicket
                : fetchInitialiseStripe;
            const clientSecret = await initialise(uuid);
            if (clientSecret) {
                return;
            }

            // No session: close the loading drawer, let the server decide if it is free,
            // and release the CTA to retry.
            closeDrawer("stripeCheckoutDrawer");
            setProceedToCheckout(false);
            if (uuid) {
                checkoutComplementary(uuid);
            }
            return;
        }

        if (selectedOption === "cinema") {
            fetchReInitialiseEwayTicket(uuid);
        } else if (pendingActionUrl) {
            if (isBypassActive) {
                try {
                    const response = await axios.get(`${API_BASE_URL}/ticket/re-initialise-eway/${uuid}`);
                    if (response.data?.data?.payment?.action_url) {
                        window.open(response.data.data.payment.action_url, "_self");
                    }
                } catch (error) {
                    console.error("Failed to re-initialise payment:", error);
                    window.open(pendingActionUrl, "_self");
                }
            } else {
                window.open(pendingActionUrl, "_self");
            }
        } else if (uuid) {
            checkoutComplementary(uuid);
        }
    };

    return (
        <>
            <Drawer
                id="termsDrawer"
                role="dialog"
                aria-modal="true"
                aria-label="Terms and conditions"
                isOpen={drawerState.termsDrawer}
                onClose={() => {
                    setTermsAccepted(false);
                    closeDrawer("termsDrawer");
                }}
                closeButtonAlignment="right"
            >
                {useAgenticAi && (
                    <CinemaBookingOption
                        hasUserClicked={hasUserClicked}
                        selectedOption={selectedOption}
                        isPolling={isPolling}
                        showLoader={showLoader}
                        showCinemaFees={showCinemaFees}
                        cinemaBookingFee={cinemaBookingFee}
                        cinemaUpgradeFee={cinemaUpgradeFee}
                        quantity={bookingFeeQuantity}
                        onOptionSelect={handleOptionSelect}
                    />
                )}

                {/* TODO update this with specific messaging about which promo has been applied. Need to work out specific wording. */}
                {/* {success && (
                    <div className="v3-booking__promo-code-msg v3-booking__promo-code-msg--success">
                        {promoCodeMessage}
                    </div>
                )} */}

                {!useAgenticAi && (
                    <VoucherNotice cinemaGroupName={cinemaGroupName} />
                )}

                {isBypassActive && (
                    <ContactDetailsForm
                        showDisplayName={shouldShowDisplayName}
                        firstName={firstName}
                        lastName={lastName}
                        purchaseEmail={purchaseEmail}
                        emailError={emailError}
                        onFirstNameChange={(e) => setFirstName(e.target.value)}
                        onLastNameChange={(e) => setLastName(e.target.value)}
                        onEmailChange={(e) => {
                            setPurchaseEmail(e.target.value);
                            setEmailError("");
                        }}
                        className="terms-drawer__form"
                        idPrefix="terms"
                        required={isBypassActive}
                    />
                )}

                {(showCinemaFees || selectedOption === "vouchers" || !isPolling) && (
                    <TermsCheckbox
                        isBypassActive={isBypassActive}
                        termsAccepted={termsAccepted}
                        onToggle={() => setTermsAccepted(!termsAccepted)}
                        openModalWithContent={openModalWithContent}
                    />
                )}
                {showTotal && (
                    <div className="terms-drawer__totals">Total Price : ${(finalTotal / 100).toFixed(2)}</div>
                )}
                <Button
                    additionalClasses="button button__primary"
                    disabled={!termsAccepted || isFetchingCinemaFees || !isContactDetailsValid}
                    onClick={handleContinue}
                >
                    {isFetchingCinemaFees ? "Checking Cinema Status..." : "Continue"}
                </Button>
                <Button
                    additionalClasses="button button__secondary"
                    onClick={() => {
                        setTermsAccepted(false);
                        closeDrawer("termsDrawer");
                    }}
                >
                    Edit cart
                </Button>
            </Drawer>
            {portal}
        </>
    );
};

export default TermsDrawer;
